import type en from '../en/users'
import type { Translation } from '../types'

/** People, roles and permissions */
const users: Translation<typeof en> = {
  "users.removed": "Gebruiker verwijderd",
  "users.removeFailed": "De gebruiker kon niet worden verwijderd",
  "users.vpnUpdated": "VPN-status bijgewerkt",
  "users.vpnFailed": "De VPN kon niet worden omgezet",
  "users.inviteSent": "Uitnodiging verstuurd",
  "users.inviteSentAgain": "Er is een nieuwe uitnodiging per e-mail verstuurd.",
  "users.resendFailed": "De uitnodiging kon niet opnieuw worden verstuurd",
  "users.reset2faDone": "Tweestapsaanmelding teruggezet",
  "users.reset2faText": "De persoon wordt overal uitgelogd en stelt het opnieuw in bij de volgende keer inloggen.",
  "users.resetFailed": "Terugzetten mislukt",
  "users.title": "Gebruikers",
  "users.count": { one: "{count} teamlid", other: "{count} teamleden" },
  "users.invite": "Gebruiker uitnodigen",
  "users.search": "Gebruikers zoeken",
  "users.searchPlaceholder": "Zoek op naam of e-mail…",
  "users.user": "Gebruiker",
  "users.role": "Rol",
  "users.lastLogin": "Laatst ingelogd",
  "users.empty": "Geen gebruikers gevonden",
  "users.adminBadge": "Beheerder",
  "users.twoFactorOn": "Tweestapsaanmelding staat aan",
  "users.never": "Nooit",
  "users.resend": "Uitnodiging opnieuw versturen",
  "users.suspendVpn": "VPN-toegang opschorten",
  "users.enableVpn": "VPN-toegang weer inschakelen",
  "users.suspendVpnConfirm": "De VPN van \"{name}\" opschorten?",
  "users.enableVpnConfirm": "De VPN van \"{name}\" weer inschakelen?",
  "users.reset2fa": "Tweestapsaanmelding terugzetten",
  "users.reset2faFor": "Tweestapsaanmelding terugzetten voor {name}",
  "users.reset2faConfirm": "Tweestapsaanmelding terugzetten voor \"{name}\"? De persoon wordt overal uitgelogd en stelt het opnieuw in bij de volgende keer inloggen.",
  "users.editAccess": "Toegang bewerken",
  "users.removeNamed": "{name} verwijderen",
  "users.removeConfirm": "Gebruiker \"{name}\" verwijderen?",
  "users.updated": "Gebruiker bijgewerkt",
  "users.addedNoMail": "Gebruiker toegevoegd, maar de uitnodiging kon niet per e-mail worden verstuurd",
  "users.addedNoMailText": "Controleer de mailinstellingen en gebruik daarna \"Uitnodiging opnieuw versturen\".",
  "users.siteAccess": "Toegang tot locaties",
  "users.allSites": "Alle locaties (ook later toegevoegde)",
  "users.noSites": "Nog geen locaties",
  "users.noneSelected": "Geen locaties gekozen — deze gebruiker ziet dan geen enkele locatie.",
  "users.updateFailed": "De gebruiker kon niet worden bijgewerkt",
  "users.editTitle": "{name} bewerken",
  "users.noRole": "Geen rol",
  "users.noRoleCustom": "Geen rol (eigen rechten)",
  "users.companyAdmin": "Beheerder van het bedrijf (volledige toegang)",
  "users.save": "Opslaan",
  "users.inviteFailed": "De gebruiker kon niet worden uitgenodigd",
  "users.inviteTitle": "Een teamlid uitnodigen",
  "users.fullName": "Volledige naam *",
  "users.fullNamePlaceholder": "Jan Jansen",
  "users.email": "E-mailadres *",
  "users.emailPlaceholder": "jan@bedrijf.nl",
  "users.inviteInfo": "Er wordt een uitnodiging per e-mail verstuurd met een link om een wachtwoord in te stellen. Het VPN-account wordt automatisch aangemaakt.",
  "users.sendInvite": "Uitnodiging versturen",
  "users.roles.deleted": "Rol verwijderd",
  "users.roles.deleteFailed": "De rol kon niet worden verwijderd",
  "users.roles.title": "Rollen",
  "users.roles.subtitle": "Sets van rechten voor uw teamleden",
  "users.roles.create": "Rol aanmaken",
  "users.roles.system": "SYSTEEM",
  "users.roles.editNamed": "{name} bewerken",
  "users.roles.deleteNamed": "{name} verwijderen",
  "users.roles.deleteConfirm": "Rol \"{name}\" verwijderen?",
  "users.roles.empty": "Nog geen rollen — maak er een aan om aan teamleden te geven",
  "users.roles.noPermissions": "Geen rechten gegeven",
  "users.roles.updated": "Rol bijgewerkt",
  "users.roles.updateFailed": "De rol kon niet worden bijgewerkt",
  "users.roles.savePermissions": "Rechten opslaan",
  "users.roles.created": "Rol aangemaakt",
  "users.roles.createFailed": "De rol kon niet worden aangemaakt",
  "users.roles.name": "Naam van de rol *",
  "users.roles.namePlaceholder": "Netwerkengineer",
  "users.roles.description": "Beschrijving",
  "users.roles.descriptionPlaceholder": "Optionele beschrijving",
  "users.roles.permissions": "Rechten",
  "users.roles.selected": { one: "{count} recht gekozen", other: "{count} rechten gekozen" },
  "users.roles.hoverHint": "Wijs een recht aan om te zien wat het toestaat",
  "users.group.sites": "Locaties",
  "users.group.monitoring": "Monitoring",
  "users.group.templates": "Sjablonen",
  "users.group.users": "Gebruikers",
  "users.group.vpn": "VPN",
  "users.group.company": "Bedrijf",
  "users.group.api": "API",
  "users.permission.SITE_VIEW": "Bekijken",
  "users.permission.SITE_CREATE": "Aanmaken",
  "users.permission.SITE_EDIT": "Bewerken",
  "users.permission.SITE_DELETE": "Verwijderen",
  "users.permission.MONITORING_VIEW": "Bekijken",
  "users.permission.TEMPLATE_VIEW": "Bekijken",
  "users.permission.TEMPLATE_CREATE": "Aanmaken",
  "users.permission.TEMPLATE_EDIT": "Bewerken",
  "users.permission.TEMPLATE_DELETE": "Verwijderen",
  "users.permission.TEMPLATE_APPLY": "Toepassen",
  "users.permission.USER_VIEW": "Bekijken",
  "users.permission.USER_CREATE": "Uitnodigen",
  "users.permission.USER_EDIT": "Bewerken",
  "users.permission.USER_DELETE": "Verwijderen",
  "users.permission.VPN_ACCESS": "Toegang",
  "users.permission.VPN_VIEW": "Bekijken",
  "users.permission.VPN_MANAGE": "Beheren",
  "users.permission.COMPANY_BRANDING": "Huisstijl",
  "users.permission.COMPANY_SETTINGS": "Instellingen",
  "users.permission.LICENSE_VIEW": "Licentie bekijken",
  "users.permission.LICENSE_MANAGE": "Licentie beheren",
  "users.permission.APIKEY_VIEW": "Bekijken",
  "users.permission.APIKEY_CREATE": "Aanmaken",
  "users.permission.APIKEY_DELETE": "Intrekken",
  "users.permissionDesc.SITE_VIEW": "De lijst met locaties en hun basisgegevens zien",
  "users.permissionDesc.SITE_CREATE": "Nieuwe locaties toevoegen",
  "users.permissionDesc.SITE_EDIT": "De instellingen en API-sleutel van een locatie wijzigen",
  "users.permissionDesc.SITE_DELETE": "Locaties definitief verwijderen",
  "users.permissionDesc.MONITORING_VIEW": "Live gegevens, apparaten en clients zien",
  "users.permissionDesc.TEMPLATE_VIEW": "De sjabloonbibliotheek doorbladeren",
  "users.permissionDesc.TEMPLATE_CREATE": "Nieuwe sjablonen maken",
  "users.permissionDesc.TEMPLATE_EDIT": "Bestaande sjablonen wijzigen",
  "users.permissionDesc.TEMPLATE_DELETE": "Sjablonen verwijderen",
  "users.permissionDesc.TEMPLATE_APPLY": "Sjablonen toepassen op locaties",
  "users.permissionDesc.USER_VIEW": "De lijst met teamleden zien",
  "users.permissionDesc.USER_CREATE": "Nieuwe leden uitnodigen",
  "users.permissionDesc.USER_EDIT": "Rollen en status wijzigen",
  "users.permissionDesc.USER_DELETE": "Leden uit het bedrijf verwijderen",
  "users.permissionDesc.VPN_ACCESS": "Het lid krijgt een VPN-account",
  "users.permissionDesc.VPN_VIEW": "De VPN-pagina en de status van de accounts zien",
  "users.permissionDesc.VPN_MANAGE": "VPN-accounts aanmaken, opschorten en vernieuwen",
  "users.permissionDesc.COMPANY_BRANDING": "Logo, kleuren en uiterlijk wijzigen",
  "users.permissionDesc.COMPANY_SETTINGS": "De instellingen en mailserver van het bedrijf beheren",
  "users.permissionDesc.LICENSE_VIEW": "Licentiestatus en limieten zien",
  "users.permissionDesc.LICENSE_MANAGE": "De licentie verlengen en wijzigen",
  "users.permissionDesc.APIKEY_VIEW": "De lijst met API-sleutels zien",
  "users.permissionDesc.APIKEY_CREATE": "Nieuwe API-sleutels aanmaken",
  "users.permissionDesc.APIKEY_DELETE": "API-sleutels intrekken",
  "users.group.rental": "Verhuur",
  "users.permission.RENTAL_VIEW": "Bekijken",
  "users.permission.RENTAL_PLAN": "Plannen",
  "users.permission.STOCK_MANAGE": "Voorraad beheren",
  "users.permission.PURCHASE_APPROVE": "Inkoop goedkeuren en versturen",
  "users.permissionDesc.RENTAL_VIEW": "Voorraad, klanten, evenementen, planning en verhuurprijzen zien",
  "users.permissionDesc.RENTAL_PLAN": "Klanten en evenementen beheren: regels, prijzen, kortingen en status",
  "users.permissionDesc.STOCK_MANAGE": "Artikelen, items, koffers, magazijnen, verplaatsingen, leveranciers, inhuur en inkooporders beheren, en kosten zien",
  "users.permissionDesc.PURCHASE_APPROVE": "Inkooporders en inhuuraanvragen goedkeuren, waarmee ze naar de leverancier gaan",
  "users.permission.MIKROTIK_PROVISION": "MikroTik-units provisioneren",
  "users.permissionDesc.MIKROTIK_PROVISION": "Een MikroTik-locatie aanmaken of vernieuwen op serienummer, het VPN-certificaat uitgeven en de configuratie ontvangen",
  "users.permission.TELTONIKA_PROVISION": "Teltonika-routers provisioneren",
  "users.permissionDesc.TELTONIKA_PROVISION": "Een Teltonika-locatie aanmaken of vernieuwen op serienummer, het VPN-certificaat uitgeven en de configuratie ontvangen",
  "users.permission.CLIENT_ACCOUNTS_MANAGE": "Klantaccounts beheren",
  "users.permissionDesc.CLIENT_ACCOUNTS_MANAGE": "De klantpersonen van een klant uitnodigen, wijzigen en verwijderen, en \"Bekijken als klant\" openen op een evenement. Alleen voor leden met toegang tot alle locaties",
}

export default users
